/**
 * Preisautomatik-Verlauf (Board „Verlauf“): Kopf „Verlauf“, eine Karte mit Ansichten, Apartment,
 * Filter und Export, Chips der aktiven Filter, Liste und Detailspalte. Reicht die Karte nicht für
 * Liste und Spalte nebeneinander (Handy, schmales Laptop), öffnen die Details als Blatt.
 */
import { useEffect, useState } from 'react';
import { problemText, useApiProblem } from '@/shared/api';
import { PageHeader } from '@/shared/components';
import { useContainerQuery } from '@/shared/hooks/useContainerQuery';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { HistoryChipsRow } from './HistoryChipsRow';
import { HistoryContent } from './HistoryContent';
import { HistoryDetailPane } from './HistoryDetailPane';
import { exportProblemText } from './historyExport';
import { HistoryFilterPanel } from './HistoryFilterPanel';
import { HistoryList } from './HistoryList';
import { historyRows } from './historyRows';
import { HistoryToolbar } from './HistoryToolbar';
import { useDynamicPricingHistory } from './useDynamicPricingHistory';
import { useHistorySearch } from './useHistorySearch';

const OFFLINE = 'Keine Verbindung – der Verlauf ist nicht geladen.';

export function DynamicPricingHistoryPage() {
  const mobile = useMediaQuery('(max-width: 767px)');
  const panel = useContainerQuery(980);
  const sheet = mobile || !panel.matches;
  const url = useHistorySearch();
  const { view } = url;
  const query = useDynamicPricingHistory(url.query, !url.scopeLoading);
  const loadProblem = useApiProblem(query.error ?? null);
  const scopeProblem = useApiProblem(url.scope.error ?? null);
  const exportProblem = exportProblemText(useApiProblem(query.exportError ?? null));
  const rows = historyRows(view, query.data);
  const [picked, setPicked] = useState<string>();
  // Ein anderer Filter macht die Auswahl und einen alten Exportfehler gegenstandslos.
  const filterKey = JSON.stringify(url.query);
  const { resetExport } = query;
  useEffect(() => {
    setPicked(undefined);
    resetExport();
  }, [filterKey, resetExport]);
  // Neben der Liste ist immer eine Zeile gewählt (Board), im Blatt erst nach dem Tippen.
  const selected = rows.find((row) => row.id === picked) ?? (sheet ? undefined : rows[0]);
  const problem = url.scopeMissing
    ? url.scope.isOffline
      ? 'Keine Verbindung – die Objekte sind nicht geladen.'
      : problemText(scopeProblem, 'Die Objekte konnten nicht geladen werden.')
    : query.data
      ? null
      : query.isError
        ? problemText(loadProblem, 'Der Verlauf konnte nicht geladen werden.')
        : query.fetchStatus === 'paused'
          ? OFFLINE
          : null;
  const loading = !problem && (url.scope.isLoading || query.isLoading);
  const moreProblem = query.isFetchNextPageError
    ? problemText(loadProblem, 'Weitere Einträge konnten nicht geladen werden.')
    : query.data && query.isPaused
      ? 'Keine Verbindung – weitere Einträge folgen, sobald du wieder online bist.'
      : null;

  return (
    <div className="w-full min-w-0">
      <PageHeader className="pb-[76px] md:pb-[76px]" title="Verlauf" />
      <section className="bg-card rounded-hero shadow-float relative z-10 mx-3 -mt-[56px] md:mx-5">
        <HistoryToolbar
          view={view}
          mobile={mobile}
          apartments={url.apartments}
          apartment={url.apartment}
          exporting={query.isExporting}
          exportDisabled={url.scopeLoading}
          onView={(next) => url.patch({ view: next })}
          onApartment={(apartment) => url.patch({ apartment })}
          onExport={query.exportHistory}
          filter={
            <HistoryFilterPanel
              view={view}
              count={url.activeFilters}
              mobile={mobile}
              current={url.draft}
              onApply={url.apply}
            />
          }
        />
        {(query.isExporting || exportProblem) && (
          <p
            role={exportProblem ? 'alert' : 'status'}
            className={`border-paper border-b px-3 py-2 text-xs md:px-[18px] ${exportProblem ? 'text-sealwax' : 'text-ink-soft'}`}
          >
            {exportProblem ?? 'Der Export läuft – das ZIP enthält alle drei Ansichten.'}
          </p>
        )}
        <HistoryChipsRow
          chips={url.chips}
          pendingTechnicalId={url.pendingTechnicalId}
          onRemove={url.removeChip}
          onClear={url.clearFilters}
        />
        <div
          ref={panel.ref}
          id="dynamic-pricing-history-panel"
          role="tabpanel"
          aria-labelledby={`history-tab-${view}`}
          className="flex min-w-0 items-stretch"
        >
          <div className="flex min-w-0 flex-1 flex-col">
            <HistoryContent
              loading={loading}
              problem={problem}
              stale={(query.isRefetchError || query.stalePages) && Boolean(query.data)}
              empty={rows.length === 0}
              retrying={query.isFetching}
              onRetry={() => (url.scopeMissing ? url.scope.retry() : void query.refetch())}
            >
              <HistoryList
                view={view}
                rows={rows}
                selectedId={selected?.id}
                mobile={mobile}
                hasMore={Boolean(query.hasMore)}
                loadingMore={query.isLoadingMore}
                loadMoreProblem={moreProblem}
                onSelect={setPicked}
                onLoadMore={() => void query.loadMore()}
              />
            </HistoryContent>
          </div>
          <HistoryDetailPane
            row={loading || problem ? undefined : selected}
            mobile={sheet}
            onClose={() => setPicked(undefined)}
          />
        </div>
      </section>
    </div>
  );
}
